import React, { useState } from 'react';
import praxisLogo from '../../assets/praxis-logo.png';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DashboardOutlined,
  PeopleOutlined,
  SchoolOutlined,
  EventOutlined,
  RateReview as RateReviewOutlined,
  SettingsOutlined,
  HistoryOutlined,
  DesktopWindowsOutlined,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import UserMenu from './UserMenu';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_WIDTH_COLLAPSED = 68;

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

const navConfig: Record<string, NavItem[]> = {
  admin: [
    { text: 'Dashboard', icon: <DashboardOutlined />, path: '/admin' },
    { text: 'Users', icon: <PeopleOutlined />, path: '/admin/users' },
    { text: 'Tech Evals', icon: <SchoolOutlined />, path: '/admin/workshops' },
    { text: 'Sessions', icon: <EventOutlined />, path: '/admin/sessions' },
    { text: 'Feedback', icon: <RateReviewOutlined />, path: '/admin/feedback' },
    { text: 'Settings', icon: <SettingsOutlined />, path: '/admin/settings' },
    { text: 'Audit Log', icon: <HistoryOutlined />, path: '/admin/audit' },
    { text: 'Desktops', icon: <DesktopWindowsOutlined />, path: '/admin/desktops' },
  ],
  instructor: [
    { text: 'Dashboard', icon: <DashboardOutlined />, path: '/instructor' },
    { text: 'Sessions', icon: <EventOutlined />, path: '/instructor/sessions' },
  ],
  student: [
    { text: 'Dashboard', icon: <DashboardOutlined />, path: '/student' },
    { text: 'Settings', icon: <SettingsOutlined />, path: '/student/settings' },
  ],
};

const accentColors: Record<string, string> = {
  admin: '#e94560',
  instructor: '#3b82f6',
  student: '#22c55e',
};

const roleLabels: Record<string, string> = {
  admin: 'Administration',
  instructor: 'Instructor',
  student: 'Student',
};

const pageTitles: Record<string, string> = {
  '/admin': 'Dashboard',
  '/admin/users': 'User Management',
  '/admin/workshops': 'Tech Evals',
  '/admin/sessions': 'Sessions',
  '/admin/settings': 'Settings',
  '/admin/audit': 'Audit Log',
  '/admin/desktops': 'Desktops',
  '/instructor': 'Dashboard',
  '/instructor/sessions': 'Sessions',
  '/instructor/workshop/new': 'Create Tech Eval',
  '/instructor/sessions/new': 'Create Session',
  '/student': 'Dashboard',
  '/student/settings': 'Settings',
};

const getPageTitle = (path: string): string => {
  if (pageTitles[path]) return pageTitles[path];
  if (path.startsWith('/admin/users/')) return 'User Detail';
  if (path.startsWith('/admin/workshops/')) return 'Tech Eval Detail';
  if (path.startsWith('/admin/sessions/')) return 'Session Detail';
  if (path.match(/\/instructor\/workshop\/.*\/edit/)) return 'Tech Eval Editor';
  if (path.match(/\/instructor\/session\/.*\/help/)) return 'Help Queue';
  if (path.match(/\/instructor\/session\/.*\/summary/)) return 'Session Summary';
  if (path.startsWith('/instructor/session/')) return 'Session';
  if (path.startsWith('/instructor/live/')) return 'Live Console';
  return 'Praxis';
};

const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentPath = location.pathname;
  const role = user?.role || 'student';
  const navItems = navConfig[role] || navConfig.student;
  const accent = accentColors[role] || accentColors.student;
  const roleLabel = roleLabels[role] || 'Student';
  const pageTitle = getPageTitle(currentPath);

  // Admin always expanded; instructor/student default collapsed
  const canCollapse = role !== 'admin';
  const [collapsed, setCollapsed] = useState(canCollapse);
  const sidebarWidth = canCollapse && collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  const roleBasePath = role === 'admin' ? '/admin' : `/${role}`;

  const isActive = (itemPath: string) => {
    if (itemPath === roleBasePath) {
      return currentPath === itemPath;
    }
    return currentPath.startsWith(itemPath);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const isCollapsed = canCollapse && collapsed && !isMobile;

  const sidebarContent = (
    <>
      {/* Logo */}
      <Box sx={{ px: isCollapsed ? 0 : 3, py: 2, textAlign: isCollapsed ? 'center' : 'left', display: 'flex', flexDirection: isCollapsed ? 'column' : 'row', alignItems: 'center', gap: isCollapsed ? 0 : 1.5 }}>
        <img src={praxisLogo} alt="Praxis" style={{ height: isCollapsed ? 32 : 40, objectFit: 'contain' }} />
        {!isCollapsed && (
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.5px', lineHeight: 1.2 }}>
              Praxis
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
              {roleLabel}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* Nav Items */}
      <List sx={{ flex: 1, px: isCollapsed ? 0.75 : 1.5, py: 1 }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          const button = (
            <ListItemButton
              onClick={() => handleNavClick(item.path)}
              sx={{
                borderRadius: '8px',
                py: 1.2,
                pl: isCollapsed ? 0 : 2,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                borderLeft: isCollapsed ? 'none' : (active ? `3px solid ${accent}` : '3px solid transparent'),
                bgcolor: active ? `${accent}14` : 'transparent',
                '&:hover': {
                  bgcolor: active ? `${accent}1F` : 'rgba(255,255,255,0.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon
                sx={{
                  color: active ? accent : 'rgba(255,255,255,0.5)',
                  minWidth: isCollapsed ? 'auto' : 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: active ? 600 : 400,
                    color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                  }}
                />
              )}
            </ListItemButton>
          );
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              {isCollapsed ? (
                <Tooltip title={item.text} placement="right" arrow>
                  {button}
                </Tooltip>
              ) : (
                button
              )}
            </ListItem>
          );
        })}
      </List>

      {/* Bottom: collapse toggle + version */}
      <Box sx={{ p: isCollapsed ? 1 : 2 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1.5 }} />
        {canCollapse && !isMobile && (
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.4)',
              width: '100%',
              borderRadius: '6px',
              py: 0.5,
              mb: 1,
              '&:hover': { color: 'rgba(255,255,255,0.7)', bgcolor: 'rgba(255,255,255,0.05)' },
            }}
          >
            {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        )}
        {!isCollapsed && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6875rem' }}>
            v0.2.0
          </Typography>
        )}
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              bgcolor: '#1a1a2e',
              borderRight: 'none',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Box
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            bgcolor: '#1a1a2e',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 1200,
            transition: 'width 0.2s ease',
            overflow: 'hidden',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Main content area */}
      <Box sx={{ flex: 1, ml: isMobile ? 0 : `${sidebarWidth}px`, display: 'flex', flexDirection: 'column', transition: 'margin-left 0.2s ease' }}>
        {/* Header bar */}
        <Box
          sx={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            px: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1100,
          }}
        >
          {isMobile && (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ mr: 1, color: 'text.primary' }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {pageTitle}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <UserMenu />
        </Box>

        {/* Page content */}
        <Box sx={{ flex: 1, p: 3, bgcolor: 'background.default' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
